

























































































import Vue, { PropType } from "vue";
import { createNamespacedHelpers } from "vuex";

import clientEmailStoreModule from "@/store/modules/clientEmail";
import clientStoreModule from "@/store/modules/client";
import { Business, Client, Role, ClientCategory, ClientEmail } from "@/types";
import ClientCategoryDropdown from "./ClientCategoryDropdown.vue";

const { mapActions: clientActions, mapGetters: clientGetters } =
  createNamespacedHelpers("CLIENT_EMAILS");

const { mapActions: emailActions } =
  createNamespacedHelpers("CLIENT_EMAIL_FORM");

interface EmailResponse {
  clientEmails: ClientEmail[];
}

interface ComponentMethods {
  validateForm(): void;
  resetForm(): void;
  cancel(): void;
  fetchData(): void;
  searchClient(q: string): void;
  addClient(): void;
  selectClient(client: Client): void;
  removeClient(client: Client): void;
  handleCategorySelection(categories: ClientCategory[]): void;
  fetchClientList(query: string): Promise<void>;
  createClientEmail(data: {
    intro: string;
    subject: string;
    clientIds: string[];
    categoryIds: string[];
  }): Promise<EmailResponse>;
}

interface ComponentData {
  valid: boolean;
  clientIds: string[];
  categoryIds: string[];
  subject: string;
  intro: string;
  searchText: string;
  filteredClients: Client[];
  selectedClients: Client[];
  introRules: ((v: string) => boolean | string)[];
  subjectRules: ((v: string) => boolean | string)[];
  clientRules: ((v: string[]) => boolean | string)[];

  titleRules: ((v: string) => boolean | string)[];
}

interface ComponentComputed {
  clientPage: { docs: Client[] };
  role: Role;
}

export default Vue.extend<
  ComponentData,
  ComponentMethods,
  ComponentComputed,
  { client?: Client }
>({
  name: "ClientEmailForm",
  components: {
    ClientCategoryDropdown,
  },
  props: {
    client: {
      type: Object as PropType<Client>,
      required: false,
    },
  },
  data() {
    return {
      valid: false,
      clientIds: [] as string[],
      categoryIds: [] as string[],
      subject: "",
      intro: "",
      searchText: "",
      filteredClients: [] as Client[],
      selectedClients: [] as Client[],
      introRules: [(v: string) => !!v || "Body field is required"],
      subjectRules: [(v: string) => !!v || "Subject field is required"],
      clientRules: [
        // function (this: { categoryIds: string[] }, v: string[]) {
        //   return (
        //     !!(v && v.length) ||
        //     !!(this.categoryIds && this.categoryIds.length) ||
        //     "Please select either clients or client groups"
        //   );
        // },
      ],
      titleRules: [(v: string) => !!v || "Title field is required"],
    };
  },
  mounted() {
    this.fetchData();
    this.resetForm();
  },
  computed: {
    ...clientGetters(["clientPage"]),
    role(): Role {
      return this.$store.getters.role;
    },
  },
  methods: {
    ...emailActions(["createClientEmail"]),
    ...clientActions(["fetchClientList"]),
    validateForm() {
      const valid = (
        this.$refs.emailForm as Element & {
          validate: () => boolean;
        }
      )?.validate();
      if (!valid) return;

      // Ensure clientIds is in sync with selectedClients
      this.clientIds = this.selectedClients.map((client) => client._id);

      // Validate at least one client or category is selected
      if (this.clientIds.length === 0 && this.categoryIds.length === 0) {
        this.$store.dispatch(
          "setToast",
          {
            title: "Selection required",
            type: "error",
            text: "Please select at least one client or category",
          },
          { root: true }
        );
        return;
      }

      // Logic for sending emails
      this.createClientEmail({
        intro: this.intro,
        subject: this.subject,
        clientIds: this.clientIds,
        categoryIds: this.categoryIds,
      }).then((emails) => {
        if (emails) this.$emit("data-saved", true);
      });
    },
    resetForm() {
      this.subject = "";
      this.clientIds = [];
      this.categoryIds = [];
      if (this.client) this.clientIds.push(this.client._id);
      this.intro = "";
      (
        this.$refs.emailForm as Element & {
          resetValidation: () => void;
        }
      )?.resetValidation();
    },
    cancel() {
      this.$emit("cancel", true);
    },
    fetchData() {
      if (this.role) {
        const bid = (this.role.business as Business)._id;
        this.fetchClientList(`?businessId=${bid}`);
      }
    },
    searchClient(q: string) {
      if (this.role) {
        const bid = (this.role.business as Business)._id;
        if (q) {
          this.fetchClientList(`?businessId=${bid}&q=${q}`).then(() => {
            // Filter out already selected clients
            this.filteredClients = this.clientPage.docs.filter(
              (client) =>
                !this.selectedClients.some(
                  (selected) => selected._id === client._id
                )
            );
          });
        } else {
          this.filteredClients = [];
        }
      }
    },
    addClient() {
      this.$emit("add-client", true);
    },
    handleCategorySelection(categories: ClientCategory[]) {
      this.categoryIds = categories.map((category) => category._id);
    },
    selectClient(client: Client) {
      if (!this.selectedClients.some((c) => c._id === client._id)) {
        this.selectedClients.push(client);
        this.clientIds.push(client._id);
        this.searchText = "";
        this.filteredClients = [];
      }
    },
    removeClient(client: Client) {
      const clientIndex = this.selectedClients.findIndex(
        (c) => c._id === client._id
      );
      if (clientIndex > -1) {
        this.selectedClients.splice(clientIndex, 1);
        const idIndex = this.clientIds.indexOf(client._id);
        if (idIndex > -1) {
          this.clientIds.splice(idIndex, 1);
        }
      }
    },
  },
  beforeCreate() {
    if (!this.$store.hasModule("CLIENT_EMAIL_FORM")) {
      this.$store.registerModule("CLIENT_EMAIL_FORM", clientEmailStoreModule);
    }
    if (!this.$store.hasModule("CLIENT_EMAILS")) {
      this.$store.registerModule("CLIENT_EMAILS", clientStoreModule);
    }
  },
  beforeDestroy() {
    this.$store.unregisterModule("CLIENT_EMAIL_FORM");
  },
});
